// AdminAthleteResultsPage.js - Direct implementation with stats cards
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import routes from 'routes';
import {
  getAthleteDetailsByAthleteId,
  getEventHistory,
  getMemberDetailByAthleteID,
  getAthlete,
  exportAthletePbReportToFile,
} from 'api/userApi';
import { getSeasons } from 'api/season';
import { getSeasonBusinessRules } from 'services/admin/SeasonService';
import { exportAndDownloadFile } from 'utils/fileDownload';
import { historyResultDataColumns } from 'config/admin/athleteDashboard';
import {
  createEventHistory,
  historyResultData,
} from 'services/admin/AthleteDashboardService';

import Loading from 'components/common/Loading';
import MetaTags from 'components/common/MetaTags';
import AdminTablePageLayout from 'components/admin/AdminTablePageLayout';
import AdminDataTable from 'components/admin/AdminDataTable';
import ResultHistoryPopup from 'components/admin/ResultHistoryPopup';
import AthleteRecentMeet from 'components/admin/AthleteDashboard/AthleteRecentMeet';
import AthletePastMeet from 'components/admin/AthleteDashboard/AthletePastMeet';
import TextButton from 'components/common/TextButton';
import ChartToolTip from 'pages/member/AthleteDashboard/ChartToolTip';
import AthleteTrainingResults from 'components/admin/AthleteDashboard/AthleteTrainingResults';
import CustomToggle from 'components/NewDesign/CustomToggle';
import CustomDropdown from 'components/NewDesign/CustomDropdown/CustomDropdown';
import CustomNewButton from 'components/NewDesign/CustomNewButton';
import useGroupLabels from 'hooks/useGroupLabels';
import { formatAthleteSeasonGroup } from 'utils/groupLabel';
import styles from './AdminAthleteResultsPage.module.css';
import { formatPoints } from 'utils/points';

const AdminAthleteResultsPage = () => {
  const { clubId, athleteId } = useParams();
  // Beside the athlete's name: the competing age and age group for the season ("9(U10)"), or the year
  // ("Y5") for a club that groups by year level.
  const { groupingBasis } = useGroupLabels(clubId);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [athleteInfo, setAthleteInfo] = useState(null);
  const [athleteDetail, setAthleteDetail] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeasonID, setSelectedSeasonID] = useState('');
  const [error, setError] = useState(null);
  const [resultView, setResultView] = useState('competition');
  const [trainingStats, setTrainingStats] = useState(null);

  // Event history popup state
  const [showPopup, setShowPopup] = useState(false);
  const [historyEventType, setHistoryEventType] = useState(null);
  const [historySeasonName, setHistorySeasonName] = useState('');
  const [historyData, setHistoryData] = useState([]);
  const [historyResults, setHistoryResults] = useState([]);
  const [historyPR, setHistoryPR] = useState('');
  const [fetchingEventHistory, setFetchingEventHistory] = useState(true);

  const fetchAthleteInfo = useCallback(async () => {
    try {
      const response = await getAthlete(clubId, athleteId);
      return response.data;
    } catch (err) {
      console.error('Error fetching athlete info:', err);
      throw err;
    }
  }, [clubId, athleteId]);

  const fetchAthleteDetails = useCallback(async () => {
    try {
      const response = await getAthleteDetailsByAthleteId(
        clubId,
        athleteId,
        selectedSeasonID || null
      );
      return response.data;
    } catch (err) {
      console.error('Error fetching athlete details:', err);
      throw err;
    }
  }, [clubId, athleteId, selectedSeasonID]);

  const fetchEventHistory = useCallback(async () => {
    try {
      setFetchingEventHistory(true);
      if (athleteId && clubId && historyEventType) {
        const response = await getEventHistory(
          clubId,
          athleteId,
          historyEventType,
          selectedSeasonID || null
        );
        return response.data;
      } else {
        return [];
      }
    } catch (err) {
      console.log('[AdminAthleteResults] Fetch event history error: ', err);
      return [];
    }
  }, [clubId, athleteId, historyEventType, selectedSeasonID]);

  const openHistoryPopup = (eventType) => {
    setShowPopup(true);
    setHistoryEventType(eventType);
  };

  const closeHistoryPopup = () => {
    setShowPopup(false);
  };

  const handleMemberDetailsClick = async () => {
    try {
      const res = await getMemberDetailByAthleteID(clubId, athleteId);
      if (res.data) {
        navigate(routes.admin.members.showMember.url(clubId, res.data.email));
      }
    } catch (err) {
      console.error('Error fetching member details:', err);
    }
  };

  const handleBackClick = () => {
    navigate(-1); // Go back to previous page
  };

  /**
   * Formats Y-axis tick values based on the unit type from the chart data
   * @param {number} value - The numeric value to format
   * @param {Array} historyData - The chart data containing unit information
   * @returns {string} - Formatted value
   */
  const formatYAxisTick = (value, historyData) => {
    // Get the unit from the first data point (all points should have the same unit)
    const unit = historyData?.[0]?.unit;

    if (unit === 's') {
      // For time events, convert seconds back to time format if over 60
      if (value >= 60) {
        const minutes = Math.floor(value / 60);
        const seconds = (value % 60).toFixed(2);
        return `${minutes}:${seconds.padStart(5, '0')}`;
      } else {
        return value.toFixed(2);
      }
    } else {
      // For distance events (unit === 'm'), show consistent decimal places
      return value.toFixed(2);
    }
  };

  // Fetch the seasons for the selector: the current season plus the most recent
  // previous season only — older seasons predate data cleanup and are not exposed.
  useEffect(() => {
    if (!clubId) return;
    getSeasons(clubId)
      .then((res) => {
        const { activeSeason, sortedSeasons } = getSeasonBusinessRules(
          res.data || []
        );
        const previousSeason = sortedSeasons.find(
          (s) => s.status !== 'Active' && s.status !== 'Draft'
        );
        setSeasons([activeSeason, previousSeason].filter(Boolean));
      })
      .catch((err) => {
        console.log('[AdminAthleteResults] Fetch seasons error: ', err);
        setSeasons([]);
      });
  }, [clubId]);

  // Fetch athlete info and details on mount
  useEffect(() => {
    const loadAthleteData = async () => {
      try {
        setLoading(true);
        // A failed load (e.g. for one season) must not brick later loads
        setError(null);
        const [info, details] = await Promise.all([
          fetchAthleteInfo(),
          fetchAthleteDetails(),
        ]);
        setAthleteInfo(info);
        setAthleteDetail(details);
        if (details.hasCompetitionResults) {
          setResultView('competition');
        } else if (details.hasTrainingResults) {
          setResultView('training');
        }
      } catch (err) {
        const serverMessage =
          err.response?.data?.message || err.response?.data?.Message;
        if (selectedSeasonID) {
          // A season switch failed: surface it and fall back to the current
          // season so the page (and the season dropdown) stays usable.
          toast.error(
            serverMessage || 'Failed to load results for the selected season'
          );
          setSelectedSeasonID('');
        } else {
          setError(serverMessage || 'Failed to load athlete information');
        }
      } finally {
        setLoading(false);
      }
    };

    if (clubId && athleteId) {
      loadAthleteData();
    }
  }, [clubId, athleteId, fetchAthleteInfo, fetchAthleteDetails, selectedSeasonID]);

  // Fetch event history when popup opens
  useEffect(() => {
    fetchEventHistory().then((eventHistory) => {
      setFetchingEventHistory(false);
      setHistorySeasonName(eventHistory.seasonName);
      if (eventHistory.results) {
        setHistoryData(createEventHistory(eventHistory.results));
        setHistoryResults(eventHistory.results);
        //setHistoryPR(getHistoryPR(eventHistory.results));
        const seasonPR = eventHistory.results?.[0]?.seasonPRValue || '';
        setHistoryPR(seasonPR);
      }
    });
  }, [fetchEventHistory]);

  // Season selection: '' = current season
  const activeSeasonID =
    seasons.find((s) => s.isCurrent)?.seasonID || seasons[0]?.seasonID || '';
  const effectiveSeasonID = selectedSeasonID || activeSeasonID;
  const seasonOptions = seasons.map((s) => ({
    value: s.seasonID,
    label: s.isCurrent ? `${s.name} (Current)` : s.name,
  }));

  const handleSeasonChange = (name, value) => {
    setSelectedSeasonID(value === activeSeasonID ? '' : value);
  };

  const handleExportPbReport = async () => {
    await exportAndDownloadFile(
      exportAthletePbReportToFile,
      [clubId, athleteId, selectedSeasonID || null],
      '',
      'The PB report was successfully exported',
      'Fetching PB report data from the server...'
    );
  };

  // Render competition stats cards
  const renderCompetitionStats = () => {
    if (!athleteDetail?.summaryStats) return null;

    const { summaryStats } = athleteDetail;
    // Points are only kept for the current season, so the card is hidden on a
    // previous-season view (the other cards are derived from results).
    const showPoints = athleteDetail.isCurrentSeason !== false;

    return (
      <div className={styles.statsGrid}>
        <div className={`${styles.statCard} ${styles.statCardRed}`}>
          <span className={styles.statNumber}>
            {summaryStats.numberOfMeets}
          </span>
          <div className={styles.statLabel}>Meets</div>
        </div>

        <div className={`${styles.statCard} ${styles.statCardGreen}`}>
          <span className={styles.statNumber}>
            {summaryStats.numberOfEvents}
          </span>
          <div className={styles.statLabel}>Events</div>
        </div>

        <div className={`${styles.statCard} ${styles.statCardPurple}`}>
          <span className={styles.statNumber}>{summaryStats.numberOfPRs}</span>
          <div className={styles.statLabel}>Season Bests</div>
        </div>

        {showPoints && (
          <div className={`${styles.statCard} ${styles.statCardBlue}`}>
            <span className={styles.statNumber}>
              {formatPoints(summaryStats.seasonPointsExact ?? summaryStats.seasonPoints)}
            </span>
            <div className={styles.statLabel}>Season Points</div>
          </div>
        )}
      </div>
    );
  };

  // Render training stats cards
  const renderTrainingStats = () => {
    if (!trainingStats) return null;

    return (
      <div className={styles.statsGrid}>
        <div className={`${styles.statCard} ${styles.statCardRed}`}>
          <span className={styles.statNumber}>
            {trainingStats.numberOfSessions}
          </span>
          <div className={styles.statLabel}>Sessions</div>
        </div>

        <div className={`${styles.statCard} ${styles.statCardGreen}`}>
          <span className={styles.statNumber}>
            {trainingStats.numberOfEvents}
          </span>
          <div className={styles.statLabel}>Events</div>
        </div>

        <div className={`${styles.statCard} ${styles.statCardPurple}`}>
          <span className={styles.statNumber}>
            {trainingStats.numberOfRuns}
          </span>
          <div className={styles.statLabel}>Total Runs</div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <AdminTablePageLayout loading={true}>
        <Loading isLoadingAdminPage={true} />
      </AdminTablePageLayout>
    );
  }

  if (error) {
    return (
      <AdminTablePageLayout loading={false}>
        <div className={styles.container}>
          <div className={styles.errorContainer}>
            <h2 className={styles.errorTitle}>Error</h2>
            <p className={styles.errorMessage}>{error}</p>
            <TextButton onClick={handleBackClick} text="Go Back" />
          </div>
        </div>
      </AdminTablePageLayout>
    );
  }

  return (
    <>
      <MetaTags
        title={`SplitFast | ${athleteInfo?.firstName} ${athleteInfo?.lastName} Results`}
      />
      <AdminTablePageLayout loading={false}>
        <div className={styles.container}>
          {/* Top navigation bar */}
          <div className={styles.topNav}>
            <div>
              <h1 className={styles.title}>
                {athleteInfo?.firstName} {athleteInfo?.lastName}
                {athleteDetail && (
                  <span className={styles.athleteMeta}>
                    {formatAthleteSeasonGroup(
                      groupingBasis,
                      athleteDetail.competingAge,
                      athleteDetail.ageGroup
                    )}{' '}
                    {athleteDetail.gender}
                  </span>
                )}
              </h1>
              <p className={styles.subtitle}>
                {athleteDetail?.seasonName
                  ? `${athleteDetail.seasonName} Season — Athlete Results & Performance`
                  : 'Athlete Results & Performance'}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '20px' }}>
              <TextButton
                onClick={handleBackClick}
                text="Athletes"
                bgColor="transparent"
                textColor="var(--new-title-color)"
              />
              <TextButton
                onClick={handleMemberDetailsClick}
                text="Member Details"
                bgColor="transparent"
                textColor="var(--new-title-color)"
              />
            </div>
          </div>
          {athleteDetail && (
            <>
              {/* Season selector + PB report download */}
              <div className={styles.seasonRow}>
                {seasonOptions.length > 1 && (
                  <div className={styles.seasonSelect}>
                    <CustomDropdown
                      label="Season"
                      value={effectiveSeasonID}
                      options={seasonOptions}
                      onChange={handleSeasonChange}
                      disabled={loading}
                    />
                  </div>
                )}
                <div className={styles.exportButtonWrap}>
                  <CustomNewButton
                    variant="info"
                    onClick={handleExportPbReport}
                    label="Download PB Report"
                    disabled={loading || !athleteDetail.hasCompetitionResults}
                  />
                </div>
              </div>

              {/* Stats cards - switch between competition and training */}
              {resultView === 'competition'
                ? renderCompetitionStats()
                : renderTrainingStats()}

              {/* Toggle between Competition and Training */}
              {athleteDetail.hasCompetitionResults &&
                athleteDetail.hasTrainingResults && (
                  <div style={{ display: 'flex', marginBottom: '20px' }}>
                    <CustomToggle
                      items={[
                        { key: 'competition', label: 'Competition' },
                        { key: 'training', label: 'Training' },
                      ]}
                      selectedItem={resultView}
                      handleToggle={setResultView}
                    />
                  </div>
                )}

              {/* Competition view */}
              <div
                style={{
                  display: resultView === 'competition' ? 'block' : 'none',
                }}
              >
                {athleteDetail.recentMeet?.results?.length > 0 ||
                athleteDetail.pastMeets?.length > 0 ? (
                  <>
                    <AthleteRecentMeet
                      recentMeet={athleteDetail.recentMeet}
                      clubId={clubId}
                      openPopup={openHistoryPopup}
                    />
                    <AthletePastMeet
                      pastMeets={athleteDetail.pastMeets}
                      seasonName={athleteDetail.seasonName}
                      openPopup={openHistoryPopup}
                      clubId={clubId}
                      isCurrentSeason={athleteDetail.isCurrentSeason !== false}
                    />
                  </>
                ) : (
                  <div className={styles.noResults}>
                    No competition results found
                  </div>
                )}
              </div>

              {/* Training view - always mounted when training results exist, hidden via CSS */}
              {athleteDetail.hasTrainingResults && (
                <div
                  style={{
                    display: resultView === 'training' ? 'block' : 'none',
                  }}
                >
                  <AthleteTrainingResults
                    clubId={clubId}
                    athleteId={athleteId}
                    seasonId={selectedSeasonID || null}
                    onStatsLoaded={setTrainingStats}
                  />
                </div>
              )}
            </>
          )}

          {/* Event History Popup */}
          <ResultHistoryPopup
            showPopup={showPopup}
            title={`${historyEventType} Event History`}
            subtitle={`${historySeasonName} season`}
            onClose={closeHistoryPopup}
            loading={fetchingEventHistory}
          >
            <h3
              className="heading1"
              style={{
                marginTop: 0,
                fontWeight: 600,
                color: 'var(--new-title-color)',
              }}
            >
              {athleteInfo?.firstName} {athleteInfo?.lastName}
            </h3>
            {historyPR && (
              <div className={styles.historyPR}>Season Best {historyPR}</div>
            )}
            <ResponsiveContainer
              width="100%"
              height="45%"
              className={styles.chartContainer}
            >
              <AreaChart
                width={400}
                height={200}
                data={historyData}
                margin={{
                  top: 40,
                  right: 0,
                  left: 0,
                  bottom: 40,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis
                  tickFormatter={(value) => formatYAxisTick(value, historyData)}
                />
                <Tooltip content={<ChartToolTip />} />
                <Area
                  type="monotone"
                  dataKey="record"
                  stroke="#cf2c47"
                  fill="#cf2c47"
                  dot={true}
                  activeDot={true}
                />
              </AreaChart>
            </ResponsiveContainer>
            {historyResults.length > 0 ? (
              <AdminDataTable
                borderType="full"
                isAddedFeatures={false}
                columns={historyResultDataColumns}
                data={historyResultData(historyResults)}
                searchInputPlaceholder=""
                headStyle={{
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  background: '#f1f1f2',
                }}
                rowStyle={{
                  gridTemplateColumns: 'repeat(4, 1fr)',
                }}
              />
            ) : (
              <div>No history found</div>
            )}
          </ResultHistoryPopup>
        </div>
      </AdminTablePageLayout>
    </>
  );
};

export default AdminAthleteResultsPage;
